import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence, useReducedMotion } from 'framer-motion';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuthStore } from '../stores/useAuthStore';
import { formatPhoneDisplay, supabase } from '../lib/supabase';
import { TermsOfServiceModal } from '../components/auth/TermsOfService';
import { AuthOtpForm, AuthPhoneForm } from '../components/auth/AuthForms';
import { EASE_OUT, KitNotice, KitTitle } from '../components/ui/premiumKit';
import { MiniNotificationTicker } from '../components/notifications/LockScreenHyperframe';
import { AuthBrandingPanel } from '../components/auth/AuthBrandingPanel';
import { safeNextPath } from '../utils/safeNextPath';
import { useSingleFlight } from '../hooks/useSingleFlight';
import { isFreshAccount } from '../lib/freshAccount';
import { beginSignupFollowUp, finishSignupFollowUp, markSignupStepDone, pauseSignupFollowUp, recordSignupConsent, resumeSignupFollowUp, saveSignupRegistrationData } from '../lib/signupFollowUp';

// 인증 직후 새 계정이 뒤처리를 기다리는 최대 시간 — 서버가 느려도 이보다 오래 붙잡지 않는다(남은 일은 쪽지로 이어짐)
const FOLLOWUP_MAX_WAIT_MS = 5000;

interface FormErrors {
    phone?: string;
    otp?: string;
}

export const AuthPage: React.FC = () => {
    // 인증 단계 관리
    const [step, setStep] = useState<'phone' | 'otp'>('phone');
    const [isSignUp, setIsSignUp] = useState<boolean>(false);

    // 폼 데이터
    const [phone, setPhone] = useState<string>('');
    const [otp, setOtp] = useState<string>('');
    const [formErrors, setFormErrors] = useState<FormErrors>({});
    const [isFormValid, setIsFormValid] = useState<boolean>(false);
    const [successMessage, setSuccessMessage] = useState<string>('');

    // 필드 터치 상태 (한 번이라도 포커스를 잃었는지 추적)
    const [touchedFields, setTouchedFields] = useState<{ phone?: boolean; otp?: boolean }>({});

    // OTP 관련 상태
    const [otpSent, setOtpSent] = useState<boolean>(false);
    const [resendCooldown, setResendCooldown] = useState<number>(0);
    const [showOtp, setShowOtp] = useState<boolean>(false);

    // 이용약관 관련 상태
    const [agreedToTerms, setAgreedToTerms] = useState<boolean>(false);
    const [isTermsModalOpen, setIsTermsModalOpen] = useState<boolean>(false);
    const [isExistingUser, setIsExistingUser] = useState<boolean>(false);
    // 인증 확인 처리 중(두 번 누름 방지·대시보드로 먼저 넘기지 않기) — 2026-10-22
    const verifyingRef = useRef<boolean>(false);
    const [verifyBusy, setVerifyBusy] = useState<boolean>(false);

    const { sendOtp, verifyOtp, user, isLoading, error, clearError, signOut } = useAuthStore();
    // 인증번호 요청(전송·재전송)은 끝날 때까지 한 번만 — 대표 7821
    const otpRequest = useSingleFlight();
    const sending = isLoading || otpRequest.busy;
    const verifyLoading = isLoading || verifyBusy;
    const navigate = useNavigate();
    const reduceMotion = useReducedMotion();
    const [searchParams] = useSearchParams();
    // 로그인 뒤 갈 곳: 보호된 화면이 넘긴 ?next= (사이트 안 주소만), 없으면 대시보드
    const afterLogin = safeNextPath(searchParams.get('next'));

    // 데이터 삭제 완료 처리
    useEffect(() => {
        const deletionComplete = searchParams.get('deletion_complete');
        const message = searchParams.get('message');

        if (deletionComplete === 'true') {
            // 로컬 세션 강제 클리어 (서버 세션은 이미 삭제됨)
            localStorage.removeItem('sb-zjfhxnrquesjysgqpqbl-auth-token');
            sessionStorage.clear();

            // 로그아웃 처리 (에러 무시)
            signOut().catch(() => {
                // 세션이 이미 없을 수 있으므로 에러 무시
            });

            // 성공 메시지 표시
            setSuccessMessage(message || '데이터가 완전히 삭제되었습니다.');
            // URL에서 파라미터 제거
            window.history.replaceState({}, document.title, '/auth');
        }
    }, [searchParams, signOut]);

    // 이미 로그인된 사용자는 대시보드로 리다이렉트
    // 단, 방금 인증한 새 계정(생성 30분 안)은 뒤처리(추천인·알림톡·동의·접속 정보)를 끝낸 뒤 handleVerifyOtp가 넘긴다 — 2026-10-22:
    // 먼저 넘기면 배포 직후 옛 화면에서 대시보드 파일이 없어 자동 새로고침이 돌고 뒤처리가 끊겼다. 서버가 느려도 5초 넘게 붙잡지 않는다.
    // 기존 회원 로그인은 예전처럼 바로 넘긴다.
    useEffect(() => {
        if (!user) return;
        if (verifyingRef.current && isFreshAccount(user.created_at)) {
            const timer = window.setTimeout(() => navigate(afterLogin), FOLLOWUP_MAX_WAIT_MS);
            return () => window.clearTimeout(timer);
        }
        navigate(afterLogin);
    }, [user, navigate, afterLogin]);

    // 폼 검증 - 터치된 필드만 검증
    useEffect(() => {
        const errors: FormErrors = {};

        if (step === 'phone') {
            // 전화번호 검증 - 터치되었고 값이 있을 때만
            if (touchedFields.phone && phone && !isValidPhoneNumber(phone)) {
                errors.phone = '올바른 전화번호를 입력해주세요 (010-1234-5678)';
            }
        } else if (step === 'otp') {
            // OTP 검증 - 터치되었고 값이 있을 때만
            if (touchedFields.otp && otp && otp.length > 0 && otp.length !== 6) {
                errors.otp = '6자리 숫자로 된 인증번호를 입력해주세요';
            }
        }

        setFormErrors(errors);

        // 폼 유효성 검사
        let isValid = false;
        if (step === 'phone') {
            // 전화번호가 있고 유효한 경우
            isValid = phone.length > 0 && isValidPhoneNumber(phone);
        } else if (step === 'otp') {
            // OTP가 정확히 6자리인 경우
            // 기존 회원은 이용약관 동의 불필요, 신규 가입자만 필요
            const otpValid = otp.length === 6 && /^\d{6}$/.test(otp);
            isValid = isExistingUser ? otpValid : (otpValid && agreedToTerms);
        }

        setIsFormValid(isValid);
    }, [phone, otp, step, touchedFields, agreedToTerms, isExistingUser]);

    // 쿨다운 타이머
    useEffect(() => {
        if (resendCooldown > 0) {
            const timer = setTimeout(() => {
                setResendCooldown(resendCooldown - 1);
            }, 1000);
            return () => clearTimeout(timer);
        }
    }, [resendCooldown]);

    // 전화번호 유효성 검사 (참고 코드 방식)
    const isValidPhoneNumber = (phoneNumber: string): boolean => {
        const cleaned = phoneNumber.replace(/\D/g, '');
        // 한국 휴대폰 번호: 11자리 숫자, 010/011/016/017/018/019로 시작
        return cleaned.length === 11 && /^(010|011|016|017|018|019)\d{8}$/.test(cleaned);
    };

    // 참고 코드의 포맷팅 로직 사용 (formatPhoneDisplay는 supabase.ts에서 import)

    // OTP 전송
    const handleSendOtp = async (e: React.FormEvent): Promise<void> => {
        e.preventDefault();

        if (!isFormValid) return;

        await otpRequest.run(async () => {
            clearError();
            setSuccessMessage('');

            try {
                // 전화번호로 기존 회원인지 확인
                const { data: userExists, error: checkError } = await supabase.rpc('check_phone_registered', {
                    p_phone: phone
                });

                if (checkError) {
                    console.error('기존 회원 확인 오류:', checkError);
                }

                const isExisting = Boolean(userExists);
                setIsExistingUser(isExisting);

                if (isExisting && isSignUp) {
                    setIsSignUp(false);
                }
                console.log('기존 회원 여부:', userExists, '전화번호:', phone.replace(/\D/g, ''));

                await sendOtp(phone, isSignUp);
                setOtpSent(true);
                setStep('otp');
                setResendCooldown(60); // 60초 쿨다운
                setSuccessMessage('인증번호가 전송되었습니다.');
            } catch (error) {
                // 에러는 store에서 처리됨
            }
        });
    };

    // OTP 검증
    const handleVerifyOtp = async (e: React.FormEvent): Promise<void> => {
        e.preventDefault();

        if (!isFormValid || verifyingRef.current) return;

        clearError();
        setSuccessMessage('');
        // 뒤처리를 끝내기 전에는 대시보드로 먼저 넘기지 않고, 끊기면 다시 열릴 때 마저 하도록 쪽지를 남긴다(2026-10-22, lib/signupFollowUp)
        verifyingRef.current = true;
        setVerifyBusy(true);
        beginSignupFollowUp({ phone, agreedToTerms, isSignUp, isExistingUser });

        try {
            const result = await verifyOtp(phone, otp);

            // 새로운 사용자인지 확인하여 데이터 수집 (방법 개선)
            console.log('🔍 데이터 수집 체크:', { isSignUp, hasUser: !!result?.user, userId: result?.user?.id });

            if (result?.user) {
                const isNewUser = isFreshAccount(result.user.created_at); // 계정은 인증번호를 보낼 때 생김 → 생성 30분 안(10/22: 10초 기준은 새 회원 90.7%를 놓침)
                console.log('🆕 신규 사용자 여부:', isNewUser);
                // 약관 동의 기록과 가입 접속 정보는 서로 기다릴 필요가 없어 함께 보낸다. 둘 다 실패해도 가입은 계속.
                await Promise.all([
                    recordSignupConsent({ agreedToTerms, isSignUp, isExistingUser, isNewUser }).then(() => markSignupStepDone('consent')),
                    saveSignupRegistrationData(result.user.id, isSignUp || isNewUser).then(() => markSignupStepDone('tracking')),
                ]);
            }
            finishSignupFollowUp();

            setSuccessMessage(isSignUp ? '회원가입이 완료되었습니다!' : '로그인되었습니다!');

            // 사용자 상태가 완전히 설정될 때까지 기다린 후 라우팅
            const checkUserAndNavigate = () => {
                const currentUser = useAuthStore.getState().user;
                if (currentUser) {
                    console.log('✅ 사용자 인증 완료, 대시보드로 이동:', currentUser.id);
                    // 2026-10-07 대표 지시로 '설치 후 이어보기'(로그인 직후 최근 분석으로 자동 이동) 삭제 — 로그인 뒤엔 원래 가려던 화면으로만 간다
                    navigate(afterLogin);
                } else {
                    console.log('⏳ 사용자 상태 대기 중...');
                    setTimeout(checkUserAndNavigate, 100); // 100ms마다 재체크
                }
            };

            setTimeout(checkUserAndNavigate, 500); // 500ms 후 시작
        } catch (error) {
            // 에러는 store에서 처리됨
            verifyingRef.current = false;
            setVerifyBusy(false);
            if ((error as Error)?.message === 'User is banned') {
                finishSignupFollowUp(); // 차단 손님은 예전처럼 동의·접속 정보를 남기지 않는다(로그아웃은 bannedUserCheck가 맡음)
            } else {
                pauseSignupFollowUp(); // 인증은 됐는데 뒤처리 중 멈췄을 수 있다 — 쪽지를 남겨 이어 하기가 마저 한다
            }
            // 인증이 됐으면(세션 있음) 예전처럼 로그인 뒤 화면으로 보낸다
            const currentUser = useAuthStore.getState().user;
            if (currentUser) {
                void resumeSignupFollowUp(currentUser);
                navigate(afterLogin);
            }
        }
    };

    // 재전송
    const handleResendOtp = async (): Promise<void> => {
        if (resendCooldown > 0) return;

        await otpRequest.run(async () => {
            clearError();
            setSuccessMessage('');

            try {
                await sendOtp(phone, isSignUp);
                setResendCooldown(60);
                setSuccessMessage('인증번호가 재전송되었습니다.');
            } catch (error) {
                // 에러는 store에서 처리됨
            }
        });
    };

    // 처음으로 돌아가기
    const handleBack = (): void => {
        setStep('phone');
        setOtp('');
        setOtpSent(false);
        setResendCooldown(0);
        clearError();
        setSuccessMessage('');
        setTouchedFields(prev => ({ ...prev, otp: false }));
    };

    // 폼 리셋
    const resetForm = (): void => {
        setPhone('');
        setOtp('');
        setFormErrors({});
        setSuccessMessage('');
        setStep('phone');
        setOtpSent(false);
        setResendCooldown(0);
        clearError();
        setTouchedFields({});
    };

    return (
        // 2026-11-03 새 디자인(대표 "다른 부분들도 구식 UI 부분들은 좀 개선해야 되지 않을까? 지금처럼말이야").
        // 기준: 대표 확정 최고 기준(10/14 안내서) — 빛 번짐 배경·유리 카드·파란 그라데이션 버튼 대신 검은 바탕·왼쪽 정렬 제목·흰 캡슐.
        // 동작(전송·확인·재전송·동의·이동)은 위 그대로이고, 글귀도 예전 그대로다.
        <div className="relative bg-black text-[#FAFAFA] pt-[env(safe-area-inset-top)]" style={{ minHeight: '100dvh' }}>
            <div className="mx-auto flex w-full max-w-6xl flex-col gap-9 px-5 pb-14 pt-6 lg:flex-row lg:items-center lg:justify-between lg:gap-20 lg:px-10 lg:py-16" style={{ minHeight: '100dvh' }}>
                {/* 왼쪽(PC) / 맨 위(휴대폰) — 브랜드·큰 제목·알림 미리보기 */}
                <AuthBrandingPanel step={step} isSignUp={isSignUp} phone={phone} />

                {/* 오른쪽 — 로그인·가입 */}
                <motion.div
                    initial={reduceMotion ? false : { opacity: 0, y: 14 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.6, delay: 0.08, ease: EASE_OUT }}
                    className="w-full lg:max-w-[440px] lg:shrink-0"
                >
                    <motion.div
                        key={`${step}-${isSignUp}`}
                        initial={reduceMotion ? false : { opacity: 0, x: step === 'otp' ? 36 : -36 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ duration: 0.42, ease: EASE_OUT }}
                    >
                        <KitTitle
                            title={step === 'phone' ? (isSignUp ? '간편 가입' : '전화번호 로그인') : '인증번호 확인'}
                            sub={step === 'phone'
                                ? isSignUp
                                    ? '전화번호만으로 간편하게 가입하세요'
                                    : '전화번호로 로그인하세요'
                                : '전송된 6자리 인증번호를 입력해주세요'}
                        />

                        {/* 알림 한 줄(성공·오류) */}
                        <AnimatePresence>
                            {(error || successMessage) && (
                                <motion.div
                                    initial={reduceMotion ? false : { opacity: 0, y: -6 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    exit={{ opacity: 0, y: -6 }}
                                    transition={{ duration: 0.25, ease: EASE_OUT }}
                                    className="mt-5"
                                >
                                    <KitNotice tone={successMessage ? 'success' : 'error'}>{successMessage || error}</KitNotice>
                                </motion.div>
                            )}
                        </AnimatePresence>

                        {/* 1단계: 전화번호 */}
                        {step === 'phone' && (
                            <AuthPhoneForm
                                phone={phone}
                                onPhoneChange={(v) => setPhone(formatPhoneDisplay(v))}
                                onPhoneBlur={() => setTouchedFields(prev => ({ ...prev, phone: true }))}
                                phoneError={formErrors.phone}
                                isFormValid={isFormValid}
                                sending={sending}
                                isSignUp={isSignUp}
                                onSubmit={handleSendOtp}
                                onToggleMode={() => {
                                    setIsSignUp(!isSignUp);
                                    resetForm();
                                }}
                            />
                        )}

                        {/* 2단계: 인증번호 */}
                        {step === 'otp' && (
                            <AuthOtpForm
                                otp={otp}
                                onOtpChange={(v) => setOtp(v.replace(/\D/g, '').slice(0, 6))}
                                onOtpBlur={() => setTouchedFields(prev => ({ ...prev, otp: true }))}
                                otpError={formErrors.otp}
                                showOtp={showOtp}
                                onToggleShowOtp={() => setShowOtp(!showOtp)}
                                isExistingUser={isExistingUser}
                                onConsentChange={setAgreedToTerms}
                                onOpenTerms={() => setIsTermsModalOpen(true)}
                                isFormValid={isFormValid}
                                verifyLoading={verifyLoading}
                                sending={sending}
                                resendCooldown={resendCooldown}
                                otpSent={otpSent}
                                onSubmit={handleVerifyOtp}
                                onBack={handleBack}
                                onResend={handleResendOtp}
                            />
                        )}
                    </motion.div>

                    {/* 휴대폰에서만 — 라이브 알림 티커 (로그인하면 받게 될 알림 미리보기) */}
                    <div className="mt-9 border-t border-white/[0.07] pt-5 lg:hidden">
                        <MiniNotificationTicker />
                    </div>
                </motion.div>
            </div>

            {/* 이용약관 모달 */}
            <TermsOfServiceModal
                isOpen={isTermsModalOpen}
                onClose={() => setIsTermsModalOpen(false)}
            />
        </div>
    );
};
